import { useState, useEffect } from 'react';
import {
  useParams,
  useNavigate,
  generatePath,
  useLocation,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ButtonSecondary,
  PermissionEnum,
  Popup,
  PermissionDenied,
  ajvResolver,
  responseErrorsResolver,
  ResponseError,
} from '@ui/src';
import { useAppSelector } from '@store';
import AutoSettingsForm from '../../components/auto-settings/form';
import {
  Schedule,
  WeekDays,
  AutoExchangeFormValues,
  Period,
  PrimaryAccount,
} from './types';
import {
  useCreateExchangeAutoSettingsPairMutation,
  useLazyGetExchangeAutoSettingsQuery,
  useLazyGetAutoExchangeAccountsCategoriesQuery,
} from './store/services';
import useGetAccountsAutocompleteList from './hooks/use-get-accounts-list';

import ROUTES from '../../routes';

import cn from './auto-settings.module.css';

const schema = {
  type: 'object',
  properties: {
    rateSourceLifetimeSeconds: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Exchange rate lifetime for auto execution is required',
    },
    exchangeCurrencyFromId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Currency from is required',
    },
    exchangeCurrencyToId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Currency to is required',
    },
  },
  required: [
    'rateSourceLifetimeSeconds',
    'exchangeCurrencyFromId',
    'exchangeCurrencyToId',
  ],
};

const AutoSettingsAddPair = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [schedule, setSchedule] = useState<Schedule[]>([
    {
      weekday: WeekDays.MONDAY,
      active: false,
      timerange: [
        {
          id: 'range-0',
          start: '',
          end: '',
        },
      ],
    },
    {
      weekday: WeekDays.TUESDAY,
      active: false,
      timerange: [
        {
          id: 'range-0',
          start: '',
          end: '',
        },
      ],
    },
    {
      weekday: WeekDays.WEDNESDAY,
      active: false,
      timerange: [
        {
          id: 'range-0',
          start: '',
          end: '',
        },
      ],
    },
    {
      weekday: WeekDays.THURSDAY,
      active: false,
      timerange: [
        {
          id: 'range-0',
          start: '',
          end: '',
        },
      ],
    },
    {
      weekday: WeekDays.FRIDAY,
      active: false,
      timerange: [
        {
          id: 'range-0',
          start: '',
          end: '',
        },
      ],
    },
    {
      weekday: WeekDays.SATURDAY,
      active: false,
      timerange: [
        {
          id: 'range-0',
          start: '',
          end: '',
        },
      ],
    },
    {
      weekday: WeekDays.SUNDAY,
      active: false,
      timerange: [
        {
          id: 'range-0',
          start: '',
          end: '',
        },
      ],
    },
  ]);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [weekdaysWithErrors, setWeekdaysWithErrors] = useState<WeekDays[]>([]);
  const [globalError, setGlobalError] = useState('');
  const [selectedPrimaryAccounts, setSelectedPrimaryAccounts] = useState<
    string[]
  >([]);
  const [primaryAccountsList, setPrimaryAccountsList] = useState<
    PrimaryAccount[]
  >([]);

  const {
    exchangeDictionaries: { activePermissions, currencies, rateSource },
    autoSettings: {
      details: { accountCategories },
    },
  } = useAppSelector((state) => state);

  const { currency } = useParams();

  const {
    control,
    reset,
    formState: { errors, isDirty },
    setValue,
    watch,
    handleSubmit,
    setError,
  } = useForm<AutoExchangeFormValues>({
    resolver: ajvResolver(schema),
  });

  const [create, { isLoading }] = useCreateExchangeAutoSettingsPairMutation();
  const [getList] = useLazyGetExchangeAutoSettingsQuery();
  const [getAccountsCategories, { isLoading: accountsCategoriesLoading }] =
    useLazyGetAutoExchangeAccountsCategoriesQuery();

  const currencyFrom = watch('exchangeCurrencyFromId');
  const currencyTo = watch('exchangeCurrencyToId');
  const exchangeType = watch('exchangeType');
  const fixedCurrencyId = watch('maxAmountCurrencyId');
  const accountsType = watch('accountsType');
  const createChildrenTransactions = watch('createChildrenTransactions');

  const navigate = useNavigate();
  const { search } = useLocation();

  const {
    autocompleteList: fromAccounts,
    onSearch: onSearchFromAccountsList,
    isLoading: fromAccountsListIsLoading,
    nextPageIsLoading: fromAccountsNextPageIsLoading,
    onScroll: onScrollFromAccounts,
    pagination: fromAccountsPagination,
  } = useGetAccountsAutocompleteList();

  const {
    autocompleteList: toAccounts,
    onSearch: onSearchToAccountsList,
    isLoading: toAccountsListIsLoading,
    nextPageIsLoading: toAccountsNextPageIsLoading,
    onScroll: onScrollToAccounts,
    pagination: toAccountsPagination,
  } = useGetAccountsAutocompleteList();

  const onSearchAutocomplete = (name: string, searchValue: string) => {
    if (name === 'fromAccount') {
      onSearchFromAccountsList(searchValue);
    } else if (name === 'toAccount') {
      onSearchToAccountsList(searchValue);
    }
  };

  const handleClose = () => {
    setPanelIsOpen(false);
    setDirtyPopupVisible(false);
    reset({});

    const url = generatePath(ROUTES.AUTO_SETTINGS_CURRENCY, {
      currency: currency as string,
    });

    setTimeout(() => {
      navigate(`${url}${search || ''}`);
    }, 100);
  };

  const onSubmit = handleSubmit(async (values) => {
    let createIsAvailable = true;

    const periods: Period[] = [];
    const activeWeekDays: WeekDays[] = [];
    const weekdaysErrors: WeekDays[] = [];

    const body: AutoExchangeFormValues = {
      active: values.active,
      rateSourceLifetimeSeconds: Number(values.rateSourceLifetimeSeconds),
      exchangeCurrencyFromId: values.exchangeCurrencyFromId,
      exchangeCurrencyToId: values.exchangeCurrencyToId,
      maxAmountCurrencyId: values.maxAmountCurrencyId,
      createChildrenTransactions: values.createChildrenTransactions,
      accountsType: values.accountsType,
    };

    if (values.minAmountPerOperation) {
      body.minAmountPerOperation = Number(values.minAmountPerOperation);
    }

    if (values.maxAmountPerOperation) {
      body.maxAmountPerOperation = Number(values.maxAmountPerOperation);
    }

    if (values.maxAmountForPeriod) {
      body.maxAmountForPeriod = Number(values.maxAmountForPeriod);
    }

    if (values.maxAmountPeriodInHours) {
      body.maxAmountPeriodInHours = Number(values.maxAmountPeriodInHours) * 24;
    }

    if (values.accountsType) {
      body.accountsType = values.accountsType;
    }

    if (values.primaryAccountIds && values.primaryAccountIds?.length) {
      body.primaryAccountIds = values.primaryAccountIds;
    }

    if (values.fromAccountId) {
      body.fromAccountId = values.fromAccountId;
    }

    if (values.toAccountId) {
      body.toAccountId = values.toAccountId;
    }

    if (values.rateSourceId) {
      body.rateSourceId = values.rateSourceId;
    }

    if (values?.targetStatus) {
      body.targetStatus = values?.targetStatus;
    }

    schedule?.forEach((item) => {
      if (item.active) {
        activeWeekDays.push(item.weekday);

        const ranges = item.timerange?.filter(
          (period) => period.start && period.end,
        );

        if (!ranges.length) {
          weekdaysErrors.push(item.weekday);
        } else {
          ranges?.forEach((range) =>
            periods.push({
              weekDay: item.weekday,
              startTime: range.start,
              endTime: range.end,
            }),
          );
        }
      }
    });

    body.activeWeekDays = activeWeekDays;
    body.periods = periods;

    if (weekdaysErrors.length) {
      createIsAvailable = false;
      setWeekdaysWithErrors(weekdaysErrors);
    } else {
      setWeekdaysWithErrors([]);
    }

    if (createIsAvailable) {
      const response = (await create(body)) as {
        error: ResponseError | { data: { message: string } };
        data?: { data: AutoExchangeFormValues };
      };

      if (response) {
        if (response?.error) {
          responseErrorsResolver({
            errors: response.error as ResponseError,
            setError,
            setGlobalError,
          });
        } else {
          getList(
            `?take=20${currency && currency !== 'all' ? `&exchangeCurrencyFromId=${currency}` : ''}${search ? search.replace('?', '&') : ''}`,
          );
          handleClose();
        }
      }
    }
  });

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.ACCOUNTS_CHART) &&
      !accountCategories
    ) {
      getAccountsCategories('all=true&include=primaryAccounts');
    }
  }, [activePermissions, accountCategories]);

  useEffect(() => {
    if (accountCategories) {
      const primaryAccounts: PrimaryAccount[] = [];

      accountCategories?.forEach((item) => {
        item.accountGroups?.forEach((group) => {
          group?.primaryAccounts?.forEach((primary) => {
            if (!primaryAccounts.find((account) => account.id === primary.id)) {
              primaryAccounts.push(primary);
            }
          });
        });
      });

      setPrimaryAccountsList(primaryAccounts);
    }
  }, [accountCategories]);

  useEffect(() => {
    if (currencyFrom && currencyTo && !fixedCurrencyId) {
      setValue('maxAmountCurrencyId', currencyFrom);
    }
  }, [currencyFrom, currencyTo, fixedCurrencyId]);

  useEffect(() => {
    if (currency !== 'all') {
      setValue('exchangeCurrencyFromId', currency as string);
    }
  }, [currency]);

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  let content;

  if (
    !activePermissions?.includes(
      PermissionEnum.SETTINGS_AUTO_EXCHANGE_CREATE_ACTION,
    )
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <AutoSettingsForm
        control={control}
        errors={errors}
        currencyFrom={currencyFrom}
        currencyTo={currencyTo}
        currencies={currencies}
        setValue={setValue}
        schedule={schedule}
        setSchedule={setSchedule}
        exchangeType={exchangeType}
        isEditable={Boolean(
          activePermissions?.includes(
            PermissionEnum.SETTINGS_AUTO_EXCHANGE_CREATE_ACTION,
          ),
        )}
        weekdaysWithErrors={weekdaysWithErrors}
        accountsType={accountsType}
        selectedPrimaryAccounts={selectedPrimaryAccounts}
        setSelectedPrimaryAccounts={setSelectedPrimaryAccounts}
        primaryAccountsList={primaryAccountsList}
        createSyncedTransaction={createChildrenTransactions}
        fromAccounts={fromAccounts}
        fromAccountsListIsLoading={fromAccountsListIsLoading}
        fromAccountsNextPageIsLoading={fromAccountsNextPageIsLoading}
        onScrollFromAccounts={onScrollFromAccounts}
        fromAccountsPagination={fromAccountsPagination}
        toAccounts={toAccounts}
        toAccountsListIsLoading={toAccountsListIsLoading}
        toAccountsNextPageIsLoading={toAccountsNextPageIsLoading}
        onScrollToAccounts={onScrollToAccounts}
        toAccountsPagination={toAccountsPagination}
        onSearchAutocomplete={onSearchAutocomplete}
        accountsCategoriesLoading={accountsCategoriesLoading}
        accountCategories={accountCategories}
        rateSource={rateSource?.map((item) => ({
          name: item.name,
          value: item.id,
          description: item.description,
        }))}
        activeSettingsVisible
        currenciesVisibile
      />
    );
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.AML}
        closeOnClickOutside
        isOpen={panelIsOpen}
        errorSidepanel={globalError}
        sidepanelExpandedKey="auto-settings-panel"
        onClose={() => {
          if (isDirty) {
            setDirtyPopupVisible(true);
          } else {
            handleClose();
          }
        }}
        title="Add Pair"
        className={cn.rateDetailsSidepanel}
        footer={
          <>
            {activePermissions?.includes(
              PermissionEnum.SETTINGS_AUTO_EXCHANGE_CREATE_ACTION,
            ) && (
              <ButtonPrimary
                text={isLoading ? 'Loading...' : 'Save'}
                onClick={onSubmit}
                disabled={!isDirty}
                bold
              />
            )}

            <ButtonSecondary
              text="Close"
              onClick={() => {
                if (isDirty) {
                  setDirtyPopupVisible(true);
                } else {
                  handleClose();
                }
              }}
              className="button-white"
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default AutoSettingsAddPair;
